/* WearSparkline & SentimentDot — iter98.99dx Phase B
 *
 * Two tiny, pure-presentation components reused across the compliance
 * queue page and the PES home compliance rows.
 *
 *  - WearSparkline: 7-bar mini chart (oldest → newest, left → right). Each
 *    bar is tone-coded vs the 22h target: emerald ≥ 80%, amber 60-79%,
 *    rose < 60%. Hovering over the SVG shows the per-day breakdown via
 *    title attributes (zero JS, native browser tooltip).
 *
 *  - SentimentDot: 1-character emoji + tone pill. positive=emerald,
 *    neutral=slate, frustrated=rose. Null = renders nothing.
 */
import React from "react";


const TARGET = 22;
const DAY_LABELS_FROM_OLDEST = ["6d", "5d", "4d", "3d", "2d", "1d", "today"];

function barTone(hours) {
    if (hours == null) return "bg-slate-200";
    const pct = (hours / TARGET) * 100;
    if (pct >= 80) return "bg-emerald-500";
    if (pct >= 60) return "bg-amber-400";
    return "bg-rose-400";
}

export function WearSparkline({ hours7d, className = "", testid }) {
    if (!hours7d || hours7d.length === 0) {
        return null;
    }
    return (
        <div
            className={`inline-flex items-end gap-[3px] h-7 ${className}`}
            data-testid={testid || "wear-sparkline"}
            aria-label="7-day wear-time"
        >
            {hours7d.map((h, i) => {
                const heightPct = Math.min(100, Math.max(8, ((h || 0) / TARGET) * 100));
                return (
                    <div
                        key={i}
                        className={`w-[5px] rounded-sm ${barTone(h)} transition-all`}
                        style={{ height: `${heightPct}%` }}
                        title={`${DAY_LABELS_FROM_OLDEST[i]}: ${(h ?? 0).toFixed(1)}h`}
                    />
                );
            })}
        </div>
    );
}


const SENTIMENT_PRESETS = {
    positive:    { emoji: "🙂", label: "Positive",   cls: "bg-emerald-100 text-emerald-700" },
    neutral:     { emoji: "😐", label: "Neutral",    cls: "bg-slate-100 text-slate-600" },
    frustrated:  { emoji: "😟", label: "Frustrated", cls: "bg-rose-100 text-rose-700" },
};

export function SentimentDot({ value, className = "", testid }) {
    if (!value || !SENTIMENT_PRESETS[value]) return null;
    const p = SENTIMENT_PRESETS[value];
    return (
        <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${p.cls} ${className}`}
            data-testid={testid || `sentiment-${value}`}
            title={`Sentiment: ${p.label}`}
        >
            <span className="text-[11px] leading-none" aria-hidden="true">{p.emoji}</span>
            <span className="hidden sm:inline">{p.label}</span>
        </span>
    );
}
